import { EyeIcon } from '@heroicons/react/24/outline'
import { trackNoticeRun } from 'components/BackgroundRuns'
import { TagMatch } from 'components/TagMatchToggle'
import { TagScopePicker } from 'components/TagScopePicker'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  cancelInsuranceNoticeRun,
  downloadInsuranceNotices,
  getInsuranceNoticeConfig,
  getInsuranceNoticeStatus,
  saveInsuranceNoticeTemplates,
  startInsuranceNoticeRun,
} from 'services'
import { Button, Tooltip } from 'stories/components'

import NoticeTemplatePreview from './NoticeTemplatePreview'

interface Level {
  key: string
  label: string
  description: string
  minDays: number
  defaultTemplate: string
}

// Generate escalating insurance-expiration notices (renewal reminder → force-placement) for
// servicing loans whose policies are expiring/expired, using docgen templates.
export default function InsuranceExpirationNoticesModal({
  onClose,
  allTags,
}: {
  onClose: () => void
  allTags: string[]
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [loadingConfig, setLoadingConfig] = useState(true)
  const [levels, setLevels] = useState<Level[]>([])
  const [templates, setTemplates] = useState<string[]>([])
  const [docgenAvailable, setDocgenAvailable] = useState(true)
  const [templateMap, setTemplateMap] = useState<Record<string, string>>({})
  const [previewLevel, setPreviewLevel] = useState<Level | null>(null)
  const [selected, setSelected] = useState<Record<string, boolean>>({})

  const [asOf, setAsOf] = useState(today)
  const [tags, setTags] = useState<string[]>([])
  const [tagMatch, setTagMatch] = useState<TagMatch>('any')
  const [excludeTags, setExcludeTags] = useState<string[]>([])
  const [attach, setAttach] = useState(true)
  const [email, setEmail] = useState(false)

  const [phase, setPhase] = useState<'form' | 'processing' | 'done'>('form')
  const [progress, setProgress] = useState<{ handled: number; total: number }>({ handled: 0, total: 0 })
  const [result, setResult] = useState<any>(null)
  const [jobId, setJobId] = useState<string>('')
  const [downloading, setDownloading] = useState(false)
  // A ref, not state: the poll closure is created once and must see the live value.
  const handedOff = useRef(false)
  // -1 once the run is working; >= 0 is how many portfolio runs are ahead of it.
  const [queuedBehind, setQueuedBehind] = useState(-1)

  useEffect(() => {
    getInsuranceNoticeConfig()
      .then((res) => {
        const lv = res?.levels || []
        setLevels(lv)
        setTemplates(res?.templates || [])
        setDocgenAvailable(res?.docgenAvailable !== false)
        // A level the tenant has mapped opens on that mapping, including one they deliberately
        // cleared. `defaultTemplate` is only the name match, a guess that can never find a template
        // whose name does not carry the level - which is most of them, once a tenant names their own.
        const saved = res?.templateMap || {}
        setTemplateMap(Object.fromEntries(lv.map((l) => [l.key, saved[l.key] ?? (l.defaultTemplate || '')])))
        setSelected(Object.fromEntries(lv.map((l) => [l.key, true])))
      })
      .catch(() => toast('Could not load the notice configuration.', { type: 'error' }))
      .finally(() => setLoadingConfig(false))
  }, [])

  /**
   * Which document a level sends is tenant configuration, not a per-run choice, so a pick is
   * saved as it is made rather than held until Generate: the modal reopens on it, and the
   * schedule runs on the same mapping. A failure has to be said out loud, because the cost of a
   * silent one is the next run going out on a template nobody chose.
   */
  const onPickTemplate = (key: string, name: string) => {
    const next = { ...templateMap, [key]: name }
    setTemplateMap(next)
    saveInsuranceNoticeTemplates(next).catch(() => toast('Could not save the template selection.', { type: 'error' }))
  }


  const activeKeys = levels.filter((l) => selected[l.key] && templateMap[l.key]).map((l) => l.key)
  const allSelected = levels.length > 0 && levels.every((l) => selected[l.key])
  const toggleAll = () => setSelected(Object.fromEntries(levels.map((l) => [l.key, !allSelected])))

  const onGenerate = async () => {
    if (!activeKeys.length) {
      toast('Select at least one notice level (with a template mapped).', { type: 'error' })
      return
    }
    setPhase('processing')
    setProgress({ handled: 0, total: 0 })
    const start: any = await startInsuranceNoticeRun({ asOf, tags, tagMatch, excludeTags, templateMap, levels: activeKeys, attach, email }).catch(
      () => null,
    )
    const id = start?.jobId
    if (!id) {
      setPhase('form')
      toast('Could not start the run.', { type: 'error' })
      return
    }
    setJobId(id)
    const poll = async () => {
      if (handedOff.current) return
      const st: any = await getInsuranceNoticeStatus(id).catch(() => null)
      if (!st || st.success === 0) {
        setPhase('form')
        toast(st?.message || 'Lost track of the run.', { type: 'error' })
        return
      }
      setQueuedBehind(st.queued ? st.queuedBehind ?? 0 : -1)
      setProgress({ handled: st.handled || 0, total: st.total || 0 })
      if (st.done) {
        if (st.error) {
          setPhase('form')
          toast(st.error, { type: 'error' })
        } else {
          setResult(st.result)
          setPhase('done')
        }
        return
      }
      setTimeout(poll, 1000)
    }
    poll()
  }

  /** Stop the run. Notices already generated are kept, so this closes rather than pretending. */
  const onCancelRun = async () => {
    if (!jobId) return
    handedOff.current = true
    setPhase('form')
    await cancelInsuranceNoticeRun(jobId).catch(() => null)
    toast('Stopping the run. Notices already generated are kept.', { type: 'warning' })
    onClose()
  }

  /** Hand the run to the background tray and close. The run carries on regardless. */
  const onRunInBackground = () => {
    if (!jobId) return
    handedOff.current = true
    trackNoticeRun(jobId, 'Insurance Expiration Notices', 'insurance')
    toast('Running in the background. You will be notified when it is ready.', { type: 'info' })
    onClose()
  }

  const onDownload = async () => {
    setDownloading(true)
    try {
      await downloadInsuranceNotices(jobId)
    } catch {
      toast('No combined document was produced for this run.', { type: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  const pct = progress.total ? Math.min(100, Math.round((progress.handled / progress.total) * 100)) : 0
  const generated = result?.generated || []
  const skipped = result?.skipped || []

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-[640px] max-w-[95vw] flex-col rounded-lg bg-white p-5 shadow-xl">
        <h2 className="mb-1 text-lg font-bold">Insurance Expiration Notices</h2>

        {phase === 'form' && (
          <>
            <p className="mb-3 text-sm text-gray-500">
              Scans servicing loans for insurance policies that are expiring soon or already expired, matches each to the
              right escalation notice, and generates it from your docgen templates.
            </p>
            {loadingConfig ? (
              <p className="py-8 text-center text-sm text-gray-400">Loading templates…</p>
            ) : (
              <div className="flex-1 overflow-auto">
                {!docgenAvailable && (
                  <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    The document generation service could not be reached, so no templates were loaded. You can still set
                    the options, but generation will fail until docgen is available.
                  </div>
                )}
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Notices to Generate
                  </span>
                  <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-gray-500">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                    Select all
                  </label>
                </div>
                <p className="mb-2 text-[11px] text-gray-400">
                  A borrower with a Preferred Language recorded gets the matching template when one exists: name it
                  after the template chosen here, as <span className="font-medium">Template Name (Spanish)</span>. Where
                  there is no such template the notice goes out on the one selected here.
                </p>
                <div className="overflow-hidden rounded border border-slate-200">
                  <table className="min-w-full text-sm">
                    <tbody>
                      {levels.map((l) => (
                        <tr key={l.key} className="border-t border-slate-100 first:border-t-0">
                          <td className="w-8 px-3 py-2 align-top">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={!!selected[l.key]}
                              onChange={(e) => setSelected((s) => ({ ...s, [l.key]: e.target.checked }))}
                            />
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className={`font-medium ${selected[l.key] ? 'text-gray-800' : 'text-gray-400'}`}>
                              {l.label}
                            </div>
                            <div className="text-[11px] text-gray-400">{l.description}</div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <select
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                                value={templateMap[l.key] || ''}
                                disabled={!selected[l.key]}
                                onChange={(e) => onPickTemplate(l.key, e.target.value)}
                              >
                                <option value="">— No template —</option>
                                {templates.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </select>
                              <Tooltip message="Preview this notice">
                                <button
                                  type="button"
                                  className="rounded p-1 text-gray-500 hover:bg-slate-100 hover:text-shade-blue disabled:opacity-30"
                                  disabled={!templateMap[l.key]}
                                  onClick={() => setPreviewLevel(l)}
                                >
                                  <EyeIcon className="h-5 w-5" />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="text-sm text-gray-600">
                    As-of Date
                    <input
                      type="date"
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      value={asOf}
                      onChange={(e) => setAsOf(e.target.value)}
                    />
                  </label>
                  <div className="text-sm text-gray-600">
                    Delivery
                    <div className="mt-1 flex flex-col gap-1">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked disabled />
                        Download combined PDF
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} />
                        Attach to each loan
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
                        Email borrowers
                      </label>
                    </div>
                  </div>
                </div>

                <TagScopePicker
                  className="mt-3"
                  allTags={allTags}
                  tags={tags}
                  onChangeTags={setTags}
                  tagMatch={tagMatch}
                  onChangeTagMatch={setTagMatch}
                  excludeTags={excludeTags}
                  onChangeExcludeTags={setExcludeTags}
                />
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button color="gray" outline onClick={onClose}>
                Cancel
              </Button>
              <Button color="blue" onClick={onGenerate} disabled={loadingConfig || !activeKeys.length}>
                Generate Notices
              </Button>
            </div>
          </>
        )}

        {phase === 'processing' && (
          <>
            <p className="mb-4 text-sm text-gray-500">
              {queuedBehind >= 0
                ? queuedBehind > 0
                  ? `Waiting for ${queuedBehind} earlier run to finish. Portfolio runs go one at a time so they cannot slow each other, or the rest of the app, down. `
                  : 'Waiting to start. '
                : ''}
              Generating insurance notices. Leave this open, or send it to the background and carry on working.
            </p>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-shade-blue transition-all duration-300"
                style={{ width: progress.total ? `${pct}%` : '8%' }}
              />
            </div>
            <p className="mt-2 text-center text-sm text-gray-600">
              {progress.total
                ? `${progress.handled.toLocaleString()} of ${progress.total.toLocaleString()} (${pct}%)`
                : 'Scanning for expiring policies…'}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button color="gray" outline onClick={onCancelRun} disabled={!jobId}>
                Cancel Run
              </Button>
              <Button color="blue" onClick={onRunInBackground} disabled={!jobId}>
                Run in Background
              </Button>
            </div>
            <p className="mt-2 text-center text-[11px] text-gray-400">
              Cancelling stops between borrowers. Notices already generated are kept.
            </p>
          </>
        )}

        {phase === 'done' && (
          <>
            <p className="mb-3 text-sm text-gray-600">
              <span className="font-semibold text-green-700">{generated.length}</span> generated
              {' · '}
              <span className="font-semibold text-amber-700">{skipped.length}</span> skipped
            </p>
            <div className="flex-1 overflow-auto">
              {generated.length > 0 && (
                <div className="mb-4">
                  <div className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Generated ({generated.length})
                  </div>
                  <div className="overflow-hidden rounded border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Loan #</th>
                          <th className="px-3 py-1.5 text-left">Borrower</th>
                          <th className="px-3 py-1.5 text-left">Notice</th>
                          <th className="px-3 py-1.5 text-left">Language</th>
                          <th className="px-3 py-1.5 text-right">Expires</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generated.map((g: any) => (
                          <tr key={g.loanNumber} className="border-t border-slate-100">
                            <td className="px-3 py-1.5">{g.loanNumber}</td>
                            <td className="px-3 py-1.5">{g.borrower}</td>
                            <td className="px-3 py-1.5">{g.level}</td>
                            {/* Blank unless the ladder found a template in the borrower's language. */}
                            <td className="px-3 py-1.5">{g.language || ''}</td>
                            <td className="px-3 py-1.5 text-right">{g.expirationDate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {skipped.length > 0 && (
                <div>
                  <div className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Skipped ({skipped.length})
                  </div>
                  <div className="overflow-hidden rounded border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Loan #</th>
                          <th className="px-3 py-1.5 text-left">Borrower</th>
                          <th className="px-3 py-1.5 text-left">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skipped.map((s: any, i: number) => (
                          <tr key={`${s.loanNumber}-${i}`} className="border-t border-slate-100">
                            <td className="px-3 py-1.5">{s.loanNumber}</td>
                            <td className="px-3 py-1.5">{s.borrower}</td>
                            <td className="px-3 py-1.5 text-amber-700">{s.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!generated.length && !skipped.length && (
                <p className="text-sm text-gray-400">No expiring insurance policies were found in scope.</p>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              {generated.length > 0 && (
                <Button color="gray" outline onClick={onDownload} loading={downloading}>
                  Download Combined PDF
                </Button>
              )}
              <Button color="blue" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>

      {previewLevel && (
        <NoticeTemplatePreview
          kind="insurance"
          level={previewLevel.key}
          levelLabel={previewLevel.label}
          template={templateMap[previewLevel.key]}
          onClose={() => setPreviewLevel(null)}
        />
      )}
    </div>
  )
}
